/**
 * Route prefetch utility to preload pages on hover
 * Eliminates first-click flash by warming up the route before user navigates
 */

import api from "../api/axiosConfig";

// Track prefetched routes to avoid duplicate loading
const prefetchedRoutes = new Set();

// Cache prefetched data by route so components can use it immediately
const routeDataCache = {};

// Map routes to their required API endpoints for preloading
// These are preloaded silently when user hovers to avoid first-click lag
const routeDataMap = {
  '/journal-entry': async () => {
    // Journal Entries needs accounting clients first, then entries
    try {
      // Step 1: Fetch accounting clients
      const clientsRes = await api.get('/accounting-clients?limit=10');
      const clients = clientsRes.data || [];
      
      // Store in cache
      routeDataCache['/journal-entry'] = { accountingClients: clients };
      
      // Step 2: If we got clients, fetch entries for the first one
      if (clients.length > 0) {
        try {
          const entriesRes = await api.get(`/journal-entries?limit=20`);
          const entries = entriesRes.data || [];
          routeDataCache['/journal-entry'].journalEntries = entries;
        } catch (e) {
          // Silently ignore entry fetch errors - at least we have clients
        }
      }
    } catch (e) {
      // Silently ignore prefetch errors
    }
  },
  '/disbursements': async () => {
    try {
      const res = await api.get('/disbursements?limit=20');
      routeDataCache['/disbursements'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/invoice': async () => {
    try {
      const res = await api.get('/invoices?limit=20');
      routeDataCache['/invoice'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/sales-order': async () => {
    try {
      const res = await api.get('/sales-orders?limit=20');
      routeDataCache['/sales-order'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/client/general-ledger': async () => {
    try {
      const res = await api.get('/general-ledger?limit=20');
      routeDataCache['/client/general-ledger'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/client/chart-of-accounts': async () => {
    try {
      const res = await api.get('/chart-of-accounts?limit=20');
      routeDataCache['/client/chart-of-accounts'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/inventory': async () => {
    try {
      const res = await api.get('/inventory?limit=20');
      routeDataCache['/inventory'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/client/products': async () => {
    try {
      const res = await api.get('/products?limit=20');
      routeDataCache['/client/products'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/purchase-order': async () => {
    try {
      const res = await api.get('/purchase-orders?limit=20');
      routeDataCache['/purchase-order'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/suppliers': async () => {
    try {
      const res = await api.get('/suppliers?limit=20');
      routeDataCache['/suppliers'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/customer': async () => {
    try {
      const res = await api.get('/customers?limit=20');
      routeDataCache['/customer'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/client/vendors': async () => {
    try {
      const res = await api.get('/vendors?limit=20');
      routeDataCache['/client/vendors'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/client/employees': async () => {
    try {
      const res = await api.get('/employee?limit=20');
      routeDataCache['/client/employees'] = { data: res.data || [] };
    } catch (e) {}
  },
  '/accounting-reports': async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.get(`/general-ledger/trial-balance?asOf=${today}`);
      routeDataCache['/accounting-reports'] = { data: res.data || {} };
    } catch (e) {}
  },
};

/**
 * Prefetch a route by triggering its initial data loads
 * Called on onMouseEnter to ensure pages are ready when clicked
 */
export const prefetchRoute = (path) => {
  // Avoid refetching the same route multiple times
  if (prefetchedRoutes.has(path)) return;
  
  prefetchedRoutes.add(path);
  
  // Use requestIdleCallback to avoid blocking user interactions
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      performRoutePrefetch(path);
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      performRoutePrefetch(path);
    }, 50);
  }
};

/**
 * Actually perform the prefetch
 * Silently loads the initial data that the page will need
 */
const performRoutePrefetch = (path) => {
  try {
    // Check if this route has an associated data preload function
    const preloadFn = routeDataMap[path];
    
    if (preloadFn) {
      // Silently call the API with a short timeout
      // We don't care about the result, just want it cached
      preloadFn().catch(() => {
        // Silently ignore errors - this is just prefetch
      });
    }
  } catch (e) {
    console.debug('Route prefetch error:', e);
  }
};

/**
 * Reset prefetch cache (useful for testing or on app reload)
 */
export const resetPrefetchCache = () => {
  prefetchedRoutes.clear();
};

/**
 * Get cached prefetch data for a route
 * Components can use this to initialize state with cached data
 */
export const getCachedRouteData = (path) => {
  return routeDataCache[path] || null;
};

/**
 * Prefetch critical routes on app initialization
 */
export const prefetchCriticalRoutes = () => {
  // Prefetch the most common routes after a short delay
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      ['/client-dashboard', '/journal-entry', '/invoice', '/client/general-ledger'].forEach(path => {
        if (!prefetchedRoutes.has(path)) {
          prefetchRoute(path);
        }
      });
    });
  }
};
